import type { PopulatedOrgMember } from "@api/types";
import { formatDateRelative } from "@lib/utils";
import { Trash2 } from "lucide-react";
import { type ReactNode, useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { getUserName, TaskUserAvatar } from "@/modules/task/shared";
import { CommentBody } from "./comment-body";
import { useCommentThread } from "./use-comment-thread";

interface CommentsThreadProps {
  gym: string;
  /** Comment on a task (comments also surface in the task's history). */
  task?: string;
  /** Comment directly on a contact: taskless notes for the whole team. */
  customer?: string;
  /** Resolved elsewhere by hosts that already fetch members; optional. */
  members?: PopulatedOrgMember[];
  placeholder?: string;
  /** Submit button label; hosts framing comments as notes pass "Add note". */
  submitLabel?: string;
  /** Shown instead of the default "No comments yet." line. */
  emptyState?: ReactNode;
  className?: string;
}

/**
 * A compact comment thread with composer: list, add, and delete-your-own.
 * Works against a task (task drawer) or directly against a contact
 * (sidebar notes).
 */
export function CommentsThread({
  gym,
  task,
  customer,
  members,
  placeholder = "Add a comment...",
  submitLabel = "Comment",
  emptyState,
  className,
}: CommentsThreadProps) {
  const { comments, busy, userById, add, remove, isOwn, dialog } = useCommentThread({
    gym,
    task,
    customer,
    members,
  });
  const [body, setBody] = useState("");

  async function handleSubmit() {
    if (await add(body)) setBody("");
  }

  return (
    <div className={cn("flex flex-col gap-4", className)}>
      {comments?.length ? (
        <ol className="flex flex-col gap-4">
          {comments.map((comment) => (
            <li key={comment.id} className="group/comment flex items-start gap-2.5">
              <TaskUserAvatar user={userById.get(comment.user)} className="mt-0.5" />
              <div className="min-w-0 flex-1">
                <p className="text-sm">
                  {/* "Unassigned" (the assignee-flavored fallback) reads wrong
                      for an author; unresolvable users get a neutral label. */}
                  <span className="font-medium">
                    {userById.has(comment.user)
                      ? getUserName(userById.get(comment.user))
                      : "Team member"}
                  </span>{" "}
                  <span className="text-xs text-muted-foreground">
                    {formatDateRelative(comment.createdAt)}
                  </span>
                </p>
                <CommentBody body={comment.body} className="leading-normal" />
              </div>
              {isOwn(comment) && (
                <button
                  type="button"
                  aria-label="Delete comment"
                  className="mt-0.5 shrink-0 rounded-md p-1 text-muted-foreground/60 opacity-0 transition-opacity hover:text-destructive focus-visible:opacity-100 group-hover/comment:opacity-100"
                  onClick={() =>
                    void remove(comment, {
                      title: "Delete comment?",
                      description:
                        "This will permanently delete the comment. This action cannot be undone.",
                    })
                  }
                >
                  <Trash2 className="size-3.5" />
                </button>
              )}
            </li>
          ))}
        </ol>
      ) : (
        (emptyState ?? <p className="text-sm text-muted-foreground">No comments yet.</p>)
      )}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void handleSubmit();
        }}
        className="flex flex-col gap-2"
      >
        <Textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder={placeholder}
          maxLength={5000}
          rows={2}
          className="resize-none border-0 bg-muted/50 placeholder:text-muted-foreground"
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              void handleSubmit();
            }
          }}
        />
        <div className="flex justify-end">
          <Button
            type="submit"
            variant="outline"
            size="sm"
            disabled={!body.trim()}
            loading={busy}
            loadingText="Posting..."
          >
            {submitLabel}
          </Button>
        </div>
      </form>
      {dialog}
    </div>
  );
}
